import React, { useCallback, useMemo, lazy, Suspense, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import './news.css';
import './newsTicker.css';

const NewsTicker = lazy(() => import('./newsTicker'));
const Navbar = lazy(() => import('./navbar'));
const NewsItem = lazy(() => import('./components/newsItem'));
const Sidebar = lazy(() => import('./components/sidebarhome'));
const TrendingSection = lazy(() => import('./components/trending'));

const apiUrl = process.env.REACT_APP_API_URL;

const fetchNews = async ({ pageParam = 1 }) => {
  const res = await fetch(
    `${apiUrl}/api/news?page=${pageParam}&per_page=15&category_id=1`
  );
  if (!res.ok) throw new Error('Failed to fetch news');
  const json = await res.json();
  return {
    items: json.data,
    total: json.pagination?.total || 0,
    currentPage: pageParam,
    hasMore: json.data.length === 15 // Has more if we got full page
  };
};

// Loader Component
const Loader = () => (
  <div className="loader">
    <div className="loader-spinner"></div>
  </div>
);

const NewsLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Use React Query's useInfiniteQuery for proper caching
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['news', 'infinite'],
    queryFn: fetchNews,
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore) return undefined;
      return lastPage.currentPage + 1;
    },
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Keep data in cache when component unmounts
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Restore scroll position when coming back from news detail
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('newsLayout_scrollPosition');
    if (savedScrollPosition && location.pathname === '/') {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScrollPosition, 10));
        // Clear the saved position after restoring
        sessionStorage.removeItem('newsLayout_scrollPosition');
      }, 100);
    }
  }, [location.pathname]);

  // Flatten all pages into single array
  const allNewsItems = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page => page.items);
  }, [data?.pages]);

  // Get total count from first page
  const totalNewsCount = data?.pages?.[0]?.total || 0;

  // Navigation handler with scroll position saving
  const handleNewsClick = useCallback((item) => {
    // Save current scroll position before navigating
    sessionStorage.setItem('newsLayout_scrollPosition', window.scrollY.toString());
    navigate(`/news/${item.id}`);
  }, [navigate]);

  // Load more function
  const loadMoreNews = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Generate news list from all loaded items
  const newsList = useMemo(
    () =>
      allNewsItems.map((item, idx) => (
        <Suspense key={item.id} fallback={<div></div>}>
          <NewsItem 
            item={item} 
            index={idx}
            onNewsClick={handleNewsClick}
          />
        </Suspense>
      )),
    [allNewsItems, handleNewsClick]
  );

  return (
    <div className="news-layout-container">
      {/* Navbar */}
      <Suspense fallback={<Loader />}>
        <Navbar />
      </Suspense>
      
      {/* Editor Info */}
      <div className="editor-info-bar">
        <p className="home-name">رئيس التحرير: كريم الزيادي</p>
      </div>

      <Suspense fallback={<Loader />}>
        <NewsTicker />
      </Suspense>

      <div className="content-wrapper">
        {/* Sidebar */}
        <Suspense fallback={<Loader />}>
          <Sidebar handleNewsClick={handleNewsClick} />
        </Suspense>

        {/* Main Content */}
        <div className="main-content">
          <div className="news-section">
            <div className="news-list">
              {/* Initial Loading */}
              {isLoading && <Loader />}
              
              {/* Error State */}
              {error && <p>Error: {error.message}</p>}
              
              {/* No News Available */}
              {!isLoading && !error && allNewsItems.length === 0 && (
                <p>لا توجد أخبار متاحة حالياً.</p>
              )}
              
              {/* News Items */}
              {newsList}
            </div>

            {/* Load More Section */}
            {!isLoading && !error && allNewsItems.length > 0 && (
              <div className="load-more-section" style={{ textAlign: 'center', padding: '20px 0' }}>
                {/* Stats */}
                <div className="news-stats" style={{ marginBottom: '20px' }}>
                  <span>
                    عرض {allNewsItems.length} من {totalNewsCount} خبر
                  </span>
                </div>

                {/* Load More Button */}
                {hasNextPage && (
                  <button
                    className="load-more-btn"
                    onClick={loadMoreNews}
                    disabled={isFetchingNextPage}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#D4A853',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isFetchingNextPage ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      margin: '0 auto',
                      opacity: isFetchingNextPage ? 0.7 : 1
                    }}
                  >
                    {isFetchingNextPage ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        جاري التحميل...
                      </>
                    ) : (
                      <>
                        عرض المزيد
                        <ChevronLeft size={16} />
                      </>
                    )}
                  </button>
                )}

                {/* End of News Message */}
                {!hasNextPage && allNewsItems.length > 0 && (
                  <p style={{ 
                    color: '#666', 
                    fontSize: '14px',
                    fontStyle: 'italic' 
                  }}>
                    تم عرض جميع الأخبار المتاحة
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar with About and Trending */}
        <Suspense fallback={<Loader />}>
          <TrendingSection handleNewsClick={handleNewsClick} />
        </Suspense>
      </div>

      {/* Footer */}
      <footer className="footer">
        <p>© جميع الحقوق محفوظة للدار العراقية 2025</p>
      </footer>
    </div>
  );
};

export default NewsLayout;